import {
  Component,
  useEffect,
  useMemo,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react"
import { Button, useToast } from "@stayframe/ui"
import { AiLoadingCard } from "@/components/AiLoadingCard"
import { cn } from "@/lib/utils"
import { useIntelligenceGate } from "@/lib/useIntelligenceGate"
import { useActivationEvent } from "@/lib/useActivationEvent"
import {
  apiClient,
  type DemandBehaviour,
  type ForecastData,
  type InsightRecommendation,
  type InsightResult,
  type PerformanceChannel,
  type PerformanceDrilldownResponse,
  type PerformancePacing,
  type PerformanceTrendPoint,
  type SeasonProfileResponse,
} from "@/lib/api"
import { createSafeCurrencyFormatter, formatPctRound } from "../overview/helpers"
import { DrilldownDrawer } from "@/components/DrilldownDrawer"
import { RevenueDrilldown } from "@/components/RevenueDrilldown"
import { OccupancyDrilldown } from "@/components/OccupancyDrilldown"
import { AdrDrilldown } from "@/components/AdrDrilldown"
import { usePerformanceData, type PeriodPreset } from "./usePerformanceData"
import { FeedbackTrigger } from "@/components/feedback"
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  CalendarClock,
  Gauge,
  RefreshCw,
  ShieldAlert,
  TriangleAlert,
} from "lucide-react"

const PERFORMANCE_AI_MESSAGES = [
  "Reviewing recent revenue trends…",
  "Comparing pace against last year…",
  "Ranking what is actually driving the result…",
  "Checking forecast confidence and booking freshness…",
  "Shaping your commercial brief…",
]

const PERIOD_TABS: Array<{ label: string; preset: PeriodPreset }> = [
  { label: "Next 12 mo", preset: "next12" },
  { label: "YTD", preset: "ytd" },
  { label: "This month", preset: "this_month" },
  { label: "Last month", preset: "last_month" },
  { label: "This year", preset: "this_year" },
  { label: "Last year", preset: "last_year" },
  { label: "Custom", preset: "custom" },
]

const TREND_METRICS = [
  { key: "revenue", label: "Revenue" },
  { key: "adr", label: "ADR" },
  { key: "booked_nights", label: "Nights" },
  { key: "revpar", label: "RevPAR" },
] as const

type TrendMetricKey = (typeof TREND_METRICS)[number]["key"]
type DrilldownKey = "revenue" | "occupancy" | "adr"
type ActionTone = "critical" | "warning" | "positive" | "neutral"
type DiagnosticTone = "critical" | "warning" | "positive" | "neutral"
type CommercialCategory = "pricing" | "occupancy" | "pace" | "channel" | "demand" | "data" | "forecast"

type ActionTarget =
  | { type: "drilldown"; kpi: DrilldownKey }
  | { type: "section"; sectionId: string }
  | { type: "sync" }

interface ActionItem {
  key: string
  category: string
  title: string
  detail: string
  consequence: string
  priority: number
  tone: ActionTone
  ctaLabel: string
  target: ActionTarget
  confidence?: string | null
}

interface DiagnosticItem {
  key: string
  label: string
  status: string
  value: string
  detail: string
  tone: DiagnosticTone
  score: number
  isPrimary?: boolean
}

interface ShiftItem {
  key: string
  label: string
  value: string
  detail: string
  tone: DiagnosticTone
}

interface HeroMetric {
  key: string
  label: string
  delta: string | null
  deltaTone: "up" | "down" | "flat"
  value: string
  detail: string
  drilldown?: DrilldownKey
  sectionId?: string
}

interface SyncModel {
  label: string
  detail: string
  tone: DiagnosticTone
  isStale: boolean
  isCritical: boolean
}

interface ForecastModel {
  label: string
  detail: string
  tone: DiagnosticTone
}

interface VerdictModel {
  headline: string
  summary: string
  tone: "ahead" | "behind" | "steady"
}

interface SectionReadout {
  headline: string
  summary: string
}

function safeDate(value: string | null | undefined): Date | null {
  const normalized = (value || "").trim()
  if (!normalized) return null
  const date = /^\d{4}-\d{2}-\d{2}$/.test(normalized)
    ? new Date(`${normalized}T12:00:00`)
    : new Date(normalized)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function formatRelativeTime(value: string | null | undefined): string {
  const date = safeDate(value)
  if (!date) return "Unknown"
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin} min ago`
  if (diffHr < 24) return `${diffHr} hrs ago`
  if (diffDay < 7) return `${diffDay} ${diffDay === 1 ? "day" : "days"} ago`
  if (diffDay < 30) return `${Math.floor(diffDay / 7)} wks ago`
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function formatShortDate(value: string | null | undefined): string {
  const date = safeDate(value)
  if (!date) return "—"
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function formatRangeLabel(from: string, to: string): string {
  return `${formatShortDate(from)} – ${formatShortDate(to)}`
}

function formatMonthLabel(period: string, style: "short" | "long" = "short"): string {
  const match = period.match(/^(\d{4})-(\d{2})/)
  if (!match) return period
  const year = Number(match[1])
  const month = Number(match[2])
  const date = new Date(year, month - 1, 1)
  if (Number.isNaN(date.getTime())) return period
  return date.toLocaleDateString(undefined, { month: style })
}

function formatDelta(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null
  const rounded = Math.abs(value) >= 10 ? Math.round(value) : Number(value.toFixed(1))
  return `${value > 0 ? "+" : ""}${rounded}%`
}

function deltaTone(value: number | null | undefined): "up" | "down" | "flat" {
  if (value == null || value === 0 || !Number.isFinite(value)) return "flat"
  return value > 0 ? "up" : "down"
}

function toneFromValue(
  value: number | null | undefined,
  positiveWhenHigh = true,
  threshold = 5,
): DiagnosticTone {
  if (value == null || !Number.isFinite(value)) return "neutral"
  if (Math.abs(value) < threshold) return "neutral"
  if (positiveWhenHigh) return value > 0 ? "positive" : "warning"
  return value > 0 ? "warning" : "positive"
}

function pctText(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—"
  return `${Math.round(value)}%`
}

function pluralize(count: number, singular: string, plural?: string): string {
  const p = plural ?? (/(ch|sh|s|x|z)$/i.test(singular) ? `${singular}es` : `${singular}s`)
  return `${count} ${count === 1 ? singular : p}`
}

function comparisonLabel(isForwardLooking: boolean, compare: "yoy" | "prev"): string {
  if (isForwardLooking) return "vs same-day last year pace"
  return compare === "prev" ? "vs prior period" : "vs last year"
}

function comparisonLegend(isForwardLooking: boolean, compare: "yoy" | "prev"): string {
  if (isForwardLooking) return "Same-day LY"
  return compare === "prev" ? "Prior period" : "Last year"
}

function getMetricDelta(
  key: "revenue" | "occupancy" | "adr" | "revpar" | "bookings" | "booked_nights",
  options: {
    isForwardLooking: boolean
    kpis: ReturnType<typeof usePerformanceData>["kpis"]
    pacing: PerformancePacing | null | undefined
  },
): number | null {
  const { isForwardLooking, kpis, pacing } = options
  if (isForwardLooking && pacing?.pace_kpi_deltas && key in pacing.pace_kpi_deltas) {
    const paceValue = pacing.pace_kpi_deltas[key]
    return typeof paceValue === "number" ? paceValue : null
  }
  const kpi = kpis?.[key]
  return typeof kpi?.delta === "number" ? kpi.delta : null
}

function getShortWindowShare(demandBehaviour: DemandBehaviour | null | undefined): number | null {
  const buckets = demandBehaviour?.lead_time_distribution || []
  if (buckets.length === 0) return null
  return buckets.slice(0, 2).reduce((sum, bucket) => sum + bucket.pct, 0)
}

function getShortStayShare(demandBehaviour: DemandBehaviour | null | undefined): number | null {
  const buckets = demandBehaviour?.stay_length_distribution || []
  if (buckets.length === 0) return null
  return buckets[0]?.pct ?? null
}

function categorizeRecommendation(rec: InsightRecommendation): CommercialCategory | null {
  const source = `${rec.action} ${rec.metric_target || ""}`.toLowerCase()
  if (/(sync|fresh|stale|data)/.test(source)) return "data"
  if (/(forecast|confidence|range)/.test(source)) return "forecast"
  if (/(adr|rate|price|pricing|discount|premium|revpar)/.test(source)) return "pricing"
  if (/(occupancy|nights|fill|calendar|availability)/.test(source)) return "occupancy"
  if (/(pace|pickup|pick-up|booking pace|bookings on books|pipeline)/.test(source)) return "pace"
  if (/(channel|airbnb|booking\.com|ota|distribution|direct)/.test(source)) return "channel"
  if (/(lead time|window|demand|weekend|stay length|booking later)/.test(source)) return "demand"
  return null
}

function buildAiRecommendationMap(result: InsightResult | null): Partial<Record<CommercialCategory, InsightRecommendation>> {
  const map: Partial<Record<CommercialCategory, InsightRecommendation>> = {}
  for (const recommendation of result?.recommendations || []) {
    const category = categorizeRecommendation(recommendation)
    if (!category || map[category]) continue
    map[category] = recommendation
  }
  return map
}

function buildSyncModel(lastSyncAt: string | null | undefined, status: string | null | undefined): SyncModel {
  if ((status || "").toLowerCase() === "never" || !lastSyncAt) {
    return {
      label: "Awaiting first sync",
      detail: "Booking data has not been refreshed yet. Treat every commercial signal as provisional.",
      tone: "critical",
      isStale: true,
      isCritical: true,
    }
  }

  const date = safeDate(lastSyncAt)
  if (!date) {
    return {
      label: "Sync time unknown",
      detail: "The booking feed returned an unreadable sync timestamp. Check freshness before acting.",
      tone: "warning",
      isStale: true,
      isCritical: false,
    }
  }

  const ageHours = (Date.now() - date.getTime()) / 3600000
  if (ageHours >= 24) {
    return {
      label: `Stale · ${formatRelativeTime(lastSyncAt)}`,
      detail: "Booking sync is old enough to lower confidence in pace, channels, and forecast.",
      tone: "critical",
      isStale: true,
      isCritical: true,
    }
  }
  if (ageHours >= 8) {
    return {
      label: `Aging · ${formatRelativeTime(lastSyncAt)}`,
      detail: "Commercial data is still usable, but today’s pick-up may be incomplete.",
      tone: "warning",
      isStale: true,
      isCritical: false,
    }
  }
  return {
    label: `Fresh · ${formatRelativeTime(lastSyncAt)}`,
    detail: "Booking pace and forecast are reading from a recent sync.",
    tone: "positive",
    isStale: false,
    isCritical: false,
  }
}

function buildForecastModel(
  forecast: ForecastData | null,
  syncModel: SyncModel,
): ForecastModel {
  if (!forecast) {
    return {
      label: "Forecast unavailable",
      detail: "Stayframe does not have enough forecast data yet.",
      tone: "neutral",
    }
  }

  if (forecast.confidence === "insufficient") {
    return {
      label: "Forecast building",
      detail: "There is not enough booking history to trust a full annual range yet.",
      tone: "warning",
    }
  }

  if (syncModel.isCritical) {
    return {
      label: `${forecast.confidence} confidence, lowered by stale sync`,
      detail: "The model has a usable range, but booking freshness is reducing trust in the latest read.",
      tone: "warning",
    }
  }

  if (forecast.confidence === "low") {
    return {
      label: "Low confidence range",
      detail: "Treat the forecast as directional only until more bookings settle in.",
      tone: "warning",
    }
  }

  if (forecast.confidence === "medium") {
    return {
      label: "Medium confidence range",
      detail: "Useful for planning this quarter, but not a hard commitment.",
      tone: "neutral",
    }
  }

  return {
    label: "High confidence range",
    detail: "Forecast and on-books coverage are aligned closely enough to plan against.",
    tone: "positive",
  }
}

function buildVerdictModel(options: {
  revenueDelta: number | null
  adrDelta: number | null
  bookedNightsDelta: number | null
  paceDelta: number | null
  topChannel: PerformanceChannel | undefined
  shortWindowShare: number | null
  compareLabel: string
  paceLabel: string
}): VerdictModel {
  const {
    revenueDelta,
    adrDelta,
    bookedNightsDelta,
    paceDelta,
    topChannel,
    shortWindowShare,
    compareLabel,
    paceLabel,
  } = options

  let headline = "Commercial performance is steady, but the next move matters."
  let tone: VerdictModel["tone"] = "steady"

  if ((paceDelta ?? 0) <= -8 && (shortWindowShare ?? 0) >= 45) {
    headline = "Pacing is behind because guests are booking later."
    tone = "behind"
  } else if ((revenueDelta ?? 0) >= 8 && (adrDelta ?? 0) <= -5) {
    headline = "Revenue is ahead, but ADR is softening."
    tone = "ahead"
  } else if ((bookedNightsDelta ?? 0) >= 4 && (paceDelta ?? 0) <= -5) {
    headline = "Occupancy is healthy, but booking pace is slipping."
    tone = "steady"
  } else if ((revenueDelta ?? 0) <= -6 && (adrDelta ?? 0) <= -4) {
    headline = "Revenue is behind, and rate strength is slipping."
    tone = "behind"
  } else if ((revenueDelta ?? 0) <= -6) {
    headline = "Revenue is behind the comparison window."
    tone = "behind"
  } else if ((revenueDelta ?? 0) >= 6 && (topChannel?.pct ?? 0) >= 70) {
    headline = "Revenue is ahead, but demand is too concentrated."
    tone = "ahead"
  } else if ((revenueDelta ?? 0) >= 6) {
    headline = "Revenue is ahead, and pace is holding."
    tone = "ahead"
  }

  const clauses: string[] = []
  if (revenueDelta != null) {
    clauses.push(`Revenue is ${formatDelta(revenueDelta)} ${compareLabel}`)
  }
  if (adrDelta != null && Math.abs(adrDelta) >= 3) {
    clauses.push(`ADR is ${formatDelta(adrDelta)} ${compareLabel}`)
  } else if (paceDelta != null && Math.abs(paceDelta) >= 3) {
    clauses.push(`${paceLabel} is ${formatDelta(paceDelta)} vs same-day last year pace`)
  }
  if ((shortWindowShare ?? 0) >= 45) {
    clauses.push(`${Math.round(shortWindowShare || 0)}% of demand is now booking inside two weeks`)
  } else if ((topChannel?.pct ?? 0) >= 70) {
    clauses.push(`${topChannel?.name || "One channel"} is driving ${Math.round(topChannel?.pct || 0)}% of booked revenue`)
  }

  return {
    headline,
    summary: clauses.slice(0, 2).join(". ") + (clauses.length > 0 ? "." : ""),
    tone,
  }
}

function buildHeroMetrics(options: {
  fmtCurrency: Intl.NumberFormat
  kpis: ReturnType<typeof usePerformanceData>["kpis"]
  revenueDelta: number | null
  paceDelta: number | null
  adrDelta: number | null
  occupancyDelta: number | null
  bookingsDelta: number | null
  pacing: PerformancePacing | null | undefined
  compareLabel: string
}): HeroMetric[] {
  const {
    fmtCurrency,
    kpis,
    revenueDelta,
    paceDelta,
    adrDelta,
    occupancyDelta,
    bookingsDelta,
    pacing,
    compareLabel,
  } = options

  return [
    {
      key: "revenue",
      label: "Revenue",
      delta: formatDelta(revenueDelta),
      deltaTone: deltaTone(revenueDelta),
      value: fmtCurrency.format(kpis?.revenue.value ?? 0),
      detail: compareLabel,
      drilldown: "revenue",
    },
    {
      key: "pace",
      label: "Pace",
      delta: formatDelta(paceDelta),
      deltaTone: deltaTone(paceDelta),
      value: pacing ? fmtCurrency.format(pacing.current_revenue) : "No pace baseline",
      detail: pacing ? "Booked revenue on the books" : "Waiting for same-day last year comparison",
      sectionId: "prf-momentum",
    },
    {
      key: "adr",
      label: "ADR",
      delta: formatDelta(adrDelta),
      deltaTone: deltaTone(adrDelta),
      value: fmtCurrency.format(kpis?.adr.value ?? 0),
      detail: compareLabel,
      drilldown: "adr",
    },
    {
      key: "occupancy",
      label: "Occupancy",
      delta: formatDelta(occupancyDelta),
      deltaTone: deltaTone(occupancyDelta),
      value: pctText(kpis?.occupancy.value),
      detail: `${Math.round(kpis?.booked_nights?.value ?? 0)} nights booked`,
      drilldown: "occupancy",
    },
    {
      key: "bookings",
      label: "Bookings",
      delta: formatDelta(bookingsDelta),
      deltaTone: deltaTone(bookingsDelta),
      value: `${Math.round(kpis?.bookings.value ?? 0)}`,
      detail: compareLabel,
      sectionId: "prf-actions",
    },
  ]
}

function buildDiagnostics(options: {
  fmtCurrency: Intl.NumberFormat
  compareLabel: string
  kpis: ReturnType<typeof usePerformanceData>["kpis"]
  revenueDelta: number | null
  occupancyDelta: number | null
  bookedNightsDelta: number | null
  adrDelta: number | null
  paceDelta: number | null
  channels: PerformanceChannel[]
  demandBehaviour: DemandBehaviour | null | undefined
}): DiagnosticItem[] {
  const {
    fmtCurrency,
    compareLabel,
    kpis,
    occupancyDelta,
    bookedNightsDelta,
    adrDelta,
    paceDelta,
    channels,
    demandBehaviour,
  } = options

  const topChannel = channels[0]
  const shortWindowShare = getShortWindowShare(demandBehaviour)
  const shortStayShare = getShortStayShare(demandBehaviour)
  const avgLead = kpis?.avg_lead_time?.value ?? null

  const diagnostics: DiagnosticItem[] = [
    {
      key: "pricing",
      label: "Pricing",
      status: (adrDelta ?? 0) <= -5 ? "Softening" : (adrDelta ?? 0) >= 5 ? "Leading" : "Holding",
      value: formatDelta(adrDelta) || fmtCurrency.format(kpis?.adr.value ?? 0),
      detail: `ADR is ${fmtCurrency.format(kpis?.adr.value ?? 0)} ${compareLabel}. RevPAR is ${formatDelta(options.revenueDelta) || pctText(kpis?.revpar.delta)} in the same window.`,
      tone: toneFromValue(adrDelta),
      score: Math.abs(adrDelta ?? 0),
    },
    {
      key: "occupancy",
      label: "Occupancy",
      status: (bookedNightsDelta ?? occupancyDelta ?? 0) <= -5 ? "Dragging" : (bookedNightsDelta ?? occupancyDelta ?? 0) >= 5 ? "Holding" : "Stable",
      value: formatDelta(bookedNightsDelta ?? occupancyDelta) || pctText(kpis?.occupancy.value),
      detail: `${Math.round(kpis?.booked_nights?.value ?? 0)} booked nights and ${pctText(kpis?.occupancy.value)} occupancy in the selected window.`,
      tone: toneFromValue(bookedNightsDelta ?? occupancyDelta),
      score: Math.abs(bookedNightsDelta ?? occupancyDelta ?? 0),
    },
    {
      key: "pace",
      label: "Pace",
      status: (paceDelta ?? 0) <= -5 ? "Behind" : (paceDelta ?? 0) >= 5 ? "Accelerating" : "Even",
      value: formatDelta(paceDelta) || "No gap",
      detail: paceDelta == null
        ? "Stayframe does not yet have a same-day last year baseline for pace."
        : `Current pace is ${formatDelta(paceDelta)} vs same-day last year.`,
      tone: toneFromValue(paceDelta),
      score: Math.abs(paceDelta ?? 0),
    },
    {
      key: "channel",
      label: "Channel quality",
      status: (topChannel?.pct ?? 0) >= 70 ? "Concentrated" : (topChannel?.pct ?? 0) >= 55 ? "Skewing" : "Balanced",
      value: topChannel ? `${topChannel.name} ${Math.round(topChannel.pct)}%` : "No channel mix",
      detail: topChannel
        ? `${topChannel.name} brings ${fmtCurrency.format(topChannel.revenue)} at ${fmtCurrency.format(topChannel.adr)} ADR${topChannel.cancellation_rate != null ? ` and ${pctText(topChannel.cancellation_rate)} cancellation` : ""}.`
        : "Channel mix will appear once bookings are present in the selected period.",
      tone: (topChannel?.pct ?? 0) >= 70 ? "warning" : (topChannel?.pct ?? 0) >= 55 ? "neutral" : "positive",
      score: Math.max(0, (topChannel?.pct ?? 0) - 45),
    },
    {
      key: "demand",
      label: "Demand timing",
      status: (shortWindowShare ?? 0) >= 45 || (avgLead ?? 0) <= 21 ? "Late-booking" : (avgLead ?? 0) >= 45 ? "Long-window" : "Balanced",
      value: shortWindowShare != null ? `${Math.round(shortWindowShare)}% inside 14d` : avgLead != null ? `${Math.round(avgLead)}d lead` : "No demand read",
      detail: shortWindowShare != null
        ? `${Math.round(shortWindowShare)}% of demand books inside two weeks${shortStayShare != null ? `, while ${Math.round(shortStayShare)}% of stays are in the shortest length bucket` : ""}.`
        : "Demand behaviour will sharpen as booking history accumulates.",
      tone: (shortWindowShare ?? 0) >= 45 || (avgLead ?? 0) <= 21 ? "warning" : "positive",
      score: Math.max(Math.max(0, (shortWindowShare ?? 0) - 30), avgLead != null ? Math.max(0, 30 - avgLead) : 0),
    },
  ]

  const primaryKey = diagnostics
    .slice()
    .sort((a, b) => b.score - a.score)[0]?.key

  return diagnostics.map((item) => ({
    ...item,
    isPrimary: item.key === primaryKey && item.score > 0,
  }))
}

function buildShiftItems(options: {
  fmtCurrency: Intl.NumberFormat
  pacing: PerformancePacing | null | undefined
  forecast: ForecastData | null
  syncModel: SyncModel
  seasonProfile: SeasonProfileResponse | null
}): ShiftItem[] {
  const items: ShiftItem[] = []

  if (options.pacing?.new_last_30d_total != null) {
    items.push({
      key: "pickup",
      label: "30-day pick-up",
      value: options.fmtCurrency.format(options.pacing.new_last_30d_total),
      detail: "New booked revenue added in the last 30 days.",
      tone: (options.pacing.new_last_30d_total ?? 0) > 0 ? "positive" : "neutral",
    })
  }

  if (options.forecast?.volatility) {
    const delta = options.forecast.volatility.delta_pct
    items.push({
      key: "forecast_move",
      label: "Forecast moved",
      value: formatDelta(delta) || "Stable",
      detail: "Change since the last forecast run.",
      tone: toneFromValue(delta),
    })
  }

  items.push({
    key: "data_confidence",
    label: "Data confidence",
    value: options.syncModel.label,
    detail: options.syncModel.detail,
    tone: options.syncModel.tone,
  })

  if (options.seasonProfile?.classification) {
    items.push({
      key: "season_profile",
      label: "Season model",
      value: options.seasonProfile.classification,
      detail: options.seasonProfile.peak_season
        ? `Peak season reads as ${options.seasonProfile.peak_season}.`
        : "Seasonality is factored into pricing and forecast interpretation.",
      tone: "neutral",
    })
  }

  return items.slice(0, 4)
}

function buildMomentumReadout(options: {
  pacing: PerformancePacing | null | undefined
  fmtCurrency: Intl.NumberFormat
}): SectionReadout {
  const monthly = (options.pacing?.pace_by_month || [])
    .filter((row) => row.pct_of_ly != null)
    .sort((a, b) => (a.pct_of_ly ?? 999) - (b.pct_of_ly ?? 999))

  const weakestMonth = monthly[0]

  if (!weakestMonth) {
    if (typeof options.pacing?.delta_pct === "number") {
      return {
        headline: options.pacing.delta_pct < 0
          ? "Pace is behind, but the weak month is not isolated yet."
          : "Pace is holding, but the curve still needs more booking history.",
        summary: "Use the chart below as directional guidance until month-by-month pacing fills in with more bookings.",
      }
    }

    return {
      headline: "Momentum needs more booking history before the risk window is clear.",
      summary: "Stayframe will call out the weakest future month once the booking curve has a usable same-day baseline.",
    }
  }

  const monthLabel = formatMonthLabel(weakestMonth.month, "long")
  const lyBookingCount = weakestMonth.ly_bookings ?? null
  const currentBookingCount = weakestMonth.current_bookings ?? null
  const bookingClause =
    lyBookingCount != null && currentBookingCount != null
      ? ` ${pluralize(currentBookingCount, "booking")} on books vs ${pluralize(lyBookingCount, "booking")} last year.`
      : ""

  if ((currentBookingCount ?? 0) === 0 && (lyBookingCount ?? 0) > 0) {
    return {
      headline: `${monthLabel} is the clearest risk window in the pace curve.`,
      summary: `${monthLabel} has no bookings on the books vs ${pluralize(lyBookingCount ?? 0, "booking")} at the same point last year.`,
    }
  }

  if ((weakestMonth.pct_of_ly ?? 100) < 80) {
    return {
      headline: `${monthLabel} is the biggest pace risk right now.`,
      summary:
        `${monthLabel} is sitting at ${Math.round(weakestMonth.pct_of_ly ?? 0)}% of last year with ` +
        `${options.fmtCurrency.format(weakestMonth.current_rotb)} on books vs ${options.fmtCurrency.format(weakestMonth.ly_rotb)} last year.` +
        bookingClause,
    }
  }

  if ((weakestMonth.pct_of_ly ?? 100) < 95) {
    return {
      headline: `${monthLabel} is the softest month, but not a full pacing break yet.`,
      summary:
        `${monthLabel} is trailing last year at ${Math.round(weakestMonth.pct_of_ly ?? 0)}% of LY.` +
        bookingClause,
    }
  }

  return {
    headline: "No single month is materially off pace right now.",
    summary: `${monthLabel} is still the softest month at ${Math.round(weakestMonth.pct_of_ly ?? 0)}% of LY, so the pace curve is broadly intact.`,
  }
}

function buildChannelReadout(options: {
  topChannel: PerformanceChannel | undefined
  kpis: ReturnType<typeof usePerformanceData>["kpis"]
  fmtCurrency: Intl.NumberFormat
}): SectionReadout {
  const { topChannel, kpis, fmtCurrency } = options

  if (!topChannel) {
    return {
      headline: "Channel quality will appear once bookings land.",
      summary: "Stayframe will surface concentration, ADR gap, and cancellation exposure as soon as the selected window has channel revenue.",
    }
  }

  const share = Math.round(topChannel.pct)
  const adrGap = kpis?.adr.value
    ? ((topChannel.adr - kpis.adr.value) / kpis.adr.value) * 100
    : null
  const metrics = [
    `${share}% of booked revenue is sitting in ${topChannel.name}`,
    `ADR is ${fmtCurrency.format(topChannel.adr)}${adrGap != null ? ` (${formatDelta(adrGap)} vs property ADR)` : ""}`,
    topChannel.cancellation_rate != null ? `cancellation is ${pctText(topChannel.cancellation_rate)}` : null,
  ].filter(Boolean)

  if (share >= 70) {
    return {
      headline: `${topChannel.name} is carrying too much of the period.`,
      summary: `${metrics.join(". ")}. Review direct mix and margin exposure before concentration becomes the next risk.`,
    }
  }

  if (share >= 55) {
    return {
      headline: `${topChannel.name} is leading the mix, but the period is still recoverable.`,
      summary: `${metrics.join(". ")}. Keep watching whether this share keeps rising or starts crowding out healthier mix.`,
    }
  }

  return {
    headline: "Revenue quality is still spread across channels.",
    summary: `${metrics.join(". ")}. No single source is dominant enough to materially weaken resilience yet.`,
  }
}

function buildDemandReadout(options: {
  demandBehaviour: DemandBehaviour | null | undefined
  shortWindowShare: number | null
  avgLead: number | null
}): SectionReadout {
  const { demandBehaviour, shortWindowShare, avgLead } = options

  if (!demandBehaviour) {
    return {
      headline: "Demand behaviour needs more booking history before it becomes actionable.",
      summary: "Booking-window and stay-length guidance will sharpen once the selected period has a broader booking sample.",
    }
  }

  const shortStayShare = getShortStayShare(demandBehaviour)
  const weekendShare = demandBehaviour.weekend_pct
  const leadLine = shortWindowShare != null
    ? `${Math.round(shortWindowShare)}% of demand is booking inside 14 days${avgLead != null ? ` and average lead time is ${Math.round(avgLead)} days` : ""}.`
    : avgLead != null
      ? `Average lead time is ${Math.round(avgLead)} days.`
      : "Demand timing is filling in, but the lead-time sample is still thin."

  const stayAction = shortStayShare != null && shortStayShare >= 50
    ? `${Math.round(shortStayShare)}% of stays sit in the shortest bucket, so adding minimum-stay friction would likely cut demand before it improves margin.`
    : shortStayShare != null && shortStayShare <= 25
      ? "Longer stays are already carrying enough share that you can stay disciplined on minimum-stay rules."
      : null

  const weekendAction = weekendShare >= 60
    ? `Weekend arrivals take ${weekendShare.toFixed(1)}% of the mix, so protect Friday and Saturday ADR and use weekdays for stimulation.`
    : weekendShare <= 35
      ? "Demand is not overly weekend-heavy, which gives more room to stimulate stay length across the full week."
      : null

  if ((shortWindowShare ?? 0) >= 45 || (avgLead ?? 999) <= 21) {
    return {
      headline: "Demand is landing late, so pricing room is tight.",
      summary: `${leadLine} ${stayAction || weekendAction || "Keep last-minute pricing responsive because slow weeks will not self-correct with much lead time."}`,
    }
  }

  if ((avgLead ?? 0) >= 45) {
    return {
      headline: "Guests are still booking far enough out to hold rate.",
      summary: `${leadLine} ${weekendAction || stayAction || "Stay disciplined on price before opening discounts too early."}`,
    }
  }

  return {
    headline: "Demand timing is balanced, but it still needs direction.",
    summary: `${leadLine} ${stayAction || weekendAction || "Use the splits below to decide whether pricing or stay rules should do more of the work."}`,
  }
}

function buildActions(options: {
  propertyId: string | null | undefined
  compareLabel: string
  fmtCurrency: Intl.NumberFormat
  kpis: ReturnType<typeof usePerformanceData>["kpis"]
  pacing: PerformancePacing | null | undefined
  syncModel: SyncModel
  forecast: ForecastData | null
  topChannel: PerformanceChannel | undefined
  shortWindowShare: number | null
  revenueDelta: number | null
  bookedNightsDelta: number | null
  occupancyDelta: number | null
  adrDelta: number | null
  paceDelta: number | null
  demandBehaviour: DemandBehaviour | null | undefined
  insightResult: InsightResult | null
}): ActionItem[] {
  const items: ActionItem[] = []
  const aiRecommendations = buildAiRecommendationMap(options.insightResult)

  const add = (item: ActionItem) => {
    if (items.some((entry) => entry.key === item.key)) return
    items.push(item)
  }

  if (options.syncModel.isStale) {
    const recommendation = aiRecommendations.data
    add({
      key: "refresh_data",
      category: "Data confidence",
      title: recommendation?.action || "Refresh booking data before acting on this week’s numbers",
      detail: options.syncModel.detail,
      consequence: "Stale booking sync can distort pace, channel mix, and the forecast range.",
      priority: options.syncModel.isCritical ? 100 : 88,
      tone: options.syncModel.isCritical ? "critical" : "warning",
      ctaLabel: "Sync now",
      target: { type: "sync" },
      confidence: recommendation?.confidence || "high",
    })
  }

  if ((options.paceDelta ?? 0) <= -5 || (options.revenueDelta ?? 0) <= -5) {
    const recommendation = aiRecommendations.pace
    add({
      key: "pace_gap",
      category: "Pace",
      title: recommendation?.action || "Close the pace gap before the next 30 days roll off",
      detail: options.pacing
        ? `${formatDelta(options.paceDelta)} vs same-day last year pace, with ${options.fmtCurrency.format(options.pacing.current_revenue)} currently on the books.`
        : `Revenue is ${formatDelta(options.revenueDelta)} ${options.compareLabel}.`,
      consequence: "If pick-up does not recover, the forecast will lean toward its lower bound.",
      priority: 92,
      tone: "critical",
      ctaLabel: "Open momentum",
      target: { type: "section", sectionId: "prf-momentum" },
      confidence: recommendation?.confidence || "high",
    })
  }

  if ((options.adrDelta ?? 0) <= -4) {
    const recommendation = aiRecommendations.pricing
    add({
      key: "pricing_soft",
      category: "Pricing",
      title: recommendation?.action || "Protect ADR before discounting becomes the story",
      detail: `${options.fmtCurrency.format(options.kpis?.adr.value ?? 0)} ADR, ${formatDelta(options.adrDelta)} ${options.compareLabel}.`,
      consequence: "Rate softness erodes margin even when occupancy looks acceptable on paper.",
      priority: 85,
      tone: "warning",
      ctaLabel: "Open ADR detail",
      target: { type: "drilldown", kpi: "adr" },
      confidence: recommendation?.confidence || "medium",
    })
  }

  if (((options.bookedNightsDelta ?? options.occupancyDelta ?? 0) <= -5)) {
    const recommendation = aiRecommendations.occupancy
    add({
      key: "occupancy_soft",
      category: "Occupancy",
      title: recommendation?.action || "Occupancy needs filling, not just rate defence",
      detail: `${Math.round(options.kpis?.booked_nights?.value ?? 0)} nights booked and ${pctText(options.kpis?.occupancy.value)} occupancy in the selected window.`,
      consequence: "Unfilled nights are now the larger drag on total revenue than price alone.",
      priority: 82,
      tone: "warning",
      ctaLabel: "Open occupancy detail",
      target: { type: "drilldown", kpi: "occupancy" },
      confidence: recommendation?.confidence || "medium",
    })
  }

  if ((options.topChannel?.pct ?? 0) >= 70) {
    const recommendation = aiRecommendations.channel
    add({
      key: "channel_risk",
      category: "Channel quality",
      title: recommendation?.action || `Reduce dependence on ${options.topChannel?.name || "one channel"}`,
      detail: `${options.topChannel?.name || "Top channel"} currently drives ${Math.round(options.topChannel?.pct || 0)}% of booked revenue.`,
      consequence: "Concentration risk makes margin, cancellation exposure, and pacing less resilient.",
      priority: 76,
      tone: "warning",
      ctaLabel: "Review channel quality",
      target: { type: "section", sectionId: "prf-channels" },
      confidence: recommendation?.confidence || "medium",
    })
  }

  if ((options.shortWindowShare ?? 0) >= 45 || (options.kpis?.avg_lead_time?.value ?? 0) <= 21) {
    const recommendation = aiRecommendations.demand
    add({
      key: "demand_window",
      category: "Demand timing",
      title: recommendation?.action || "Guests are booking later, leaving less pricing room",
      detail: options.shortWindowShare != null
        ? `${Math.round(options.shortWindowShare)}% of demand is landing inside two weeks.`
        : `${Math.round(options.kpis?.avg_lead_time?.value ?? 0)} day average lead time.`,
      consequence: "Compressed booking windows force faster pricing decisions and reduce time to recover slow weeks.",
      priority: 72,
      tone: "warning",
      ctaLabel: "Open demand timing",
      target: { type: "section", sectionId: "prf-demand" },
      confidence: recommendation?.confidence || "medium",
    })
  }

  if (options.forecast && (options.forecast.confidence === "low" || options.forecast.confidence === "insufficient")) {
    const recommendation = aiRecommendations.forecast
    add({
      key: "forecast_confidence",
      category: "Forecast confidence",
      title: recommendation?.action || "Treat the forecast as directional until confidence improves",
      detail: options.forecast.confidence === "insufficient"
        ? "Stayframe needs more booking history before the model can carry a reliable annual range."
        : "The forecast range is available, but confidence is still low.",
      consequence: "Use the range for scenario planning, not for hard revenue commitments.",
      priority: 64,
      tone: "neutral",
      ctaLabel: "Open forecast",
      target: { type: "section", sectionId: "prf-forecast" },
      confidence: recommendation?.confidence || options.forecast.confidence,
    })
  }

  if (items.length === 0) {
    add({
      key: "steady_watch",
      category: "Commercial watch",
      title: "Performance is holding. Keep pressure on pace and rate discipline.",
      detail: "There is no immediate commercial fire, but the next demand read still matters.",
      consequence: "A steady week is the right moment to tighten rate quality before pressure returns.",
      priority: 50,
      tone: "positive",
      ctaLabel: "Open momentum",
      target: { type: "section", sectionId: "prf-momentum" },
      confidence: "medium",
    })
  }

  return items.sort((a, b) => b.priority - a.priority)
}

function buildBriefLead(
  verdict: VerdictModel,
  insightResult: InsightResult | null,
): string {
  return (
    insightResult?.human_summary?.what_matters_now?.[0]
    || insightResult?.human_summary?.drivers?.[0]
    || verdict.summary
    || verdict.headline
  )
}

function buildBriefDrivers(
  diagnostics: DiagnosticItem[],
  insightResult: InsightResult | null,
): Array<{ label: string; value: string }> {
  const aiDrivers = (insightResult?.drivers || []).slice(0, 3).map((driver) => ({
    label: driver.confidence,
    value: driver.hypothesis,
  }))
  if (aiDrivers.length > 0) return aiDrivers

  return diagnostics
    .slice()
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((item) => ({
      label: item.label,
      value: item.detail,
    }))
}

function toTrendValue(point: PerformanceTrendPoint, key: TrendMetricKey): number {
  if (key === "booked_nights") return point.booked_nights ?? 0
  return point[key]
}

function formatChartAxis(value: number, currencyFormatter: Intl.NumberFormat, isCurrency: boolean): string {
  if (!Number.isFinite(value)) return "0"
  if (!isCurrency) return `${Math.round(value)}`
  if (Math.abs(value) >= 1000) return `${Math.round(value / 1000)}k`
  return currencyFormatter.format(value)
}

interface TrendPanelProps {
  activeMetric: TrendMetricKey
  onMetricChange: (metric: TrendMetricKey) => void
  trend: PerformanceTrendPoint[]
  comparisonTrend: PerformanceTrendPoint[]
  comparisonLegendLabel: string
  compareLabel: string
  fmtCurrency: Intl.NumberFormat
  pacing: PerformancePacing | null | undefined
  periodLabel: string
}

function TrendPanel({
  activeMetric,
  onMetricChange,
  trend,
  comparisonTrend,
  comparisonLegendLabel,
  compareLabel,
  fmtCurrency,
  pacing,
  periodLabel,
}: TrendPanelProps) {
  const isCurrencyMetric = activeMetric === "revenue" || activeMetric === "adr" || activeMetric === "revpar"
  const hasComparison = comparisonTrend.length > 0

  const chartData = trend.map((point, index) => {
    const comparison = comparisonTrend[index]
    return {
      month: formatMonthLabel(point.period),
      current: toTrendValue(point, activeMetric),
      comparison: comparison ? toTrendValue(comparison, activeMetric) : undefined,
    }
  })

  const totalCurrent = trend.reduce((sum, point) => sum + toTrendValue(point, activeMetric), 0)
  const totalComparison = comparisonTrend.reduce((sum, point) => sum + toTrendValue(point, activeMetric), 0)
  const totalDelta = totalComparison > 0 ? ((totalCurrent - totalComparison) / totalComparison) * 100 : null
  const momentumReadout = buildMomentumReadout({ pacing, fmtCurrency })

  const weakestPaceMonths = (pacing?.pace_by_month || [])
    .filter((row) => row.pct_of_ly != null)
    .sort((a, b) => (a.pct_of_ly ?? 999) - (b.pct_of_ly ?? 999))
    .slice(0, 4)

  return (
    <div className="prf-panel prf-panel--trend" id="prf-momentum">
      <div className="prf-panel__header">
        <div>
          <span className="prf-section-kicker">Momentum & pace</span>
          <h2 className="prf-panel__title">How the commercial picture is moving</h2>
        </div>
        <div className="prf-segmented" role="tablist" aria-label="Trend metric">
          {TREND_METRICS.map((metric) => (
            <button
              key={metric.key}
              type="button"
              className={cn("prf-segmented__button", activeMetric === metric.key && "prf-segmented__button--active")}
              onClick={() => onMetricChange(metric.key)}
            >
              {metric.label}
            </button>
          ))}
        </div>
      </div>

      <div className="prf-panel__intro prf-panel__intro--stack">
        <p className="prf-panel__body">
          {activeMetric === "revenue" && totalDelta != null
            ? `Booked revenue is ${formatDelta(totalDelta)} ${compareLabel} across ${periodLabel.toLowerCase()}.`
            : activeMetric === "adr" && totalDelta != null
              ? `Rate strength is ${formatDelta(totalDelta)} ${compareLabel}.`
              : activeMetric === "booked_nights" && totalDelta != null
                ? `Booked nights are ${formatDelta(totalDelta)} ${compareLabel}.`
                : activeMetric === "revpar" && totalDelta != null
                ? `RevPAR is ${formatDelta(totalDelta)} ${compareLabel}.`
                  : `Use the trend lens below to compare the selected window against ${comparisonLegendLabel.toLowerCase()}.`}
        </p>
        <div className="prf-panel__callout">
          <p className="prf-panel__callout-label">Interpretation</p>
          <h3 className="prf-panel__callout-title">{momentumReadout.headline}</h3>
          <p className="prf-panel__callout-body">{momentumReadout.summary}</p>
        </div>
      </div>

      <div className="prf-trend-layout">
        <div className="prf-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(39, 104, 74, 0.08)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "rgba(65, 59, 49, 0.58)" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "rgba(65, 59, 49, 0.58)" }}
                axisLine={false}
                tickLine={false}
                width={isCurrencyMetric ? 56 : 36}
                tickFormatter={(value) => formatChartAxis(Number(value), fmtCurrency, isCurrencyMetric)}
              />
              <Tooltip
                contentStyle={{
                  background: "rgba(255, 255, 255, 0.96)",
                  border: "1px solid rgba(39, 104, 74, 0.1)",
                  borderRadius: "14px",
                  fontSize: "12px",
                }}
                formatter={(value: number | undefined, name: string | undefined) => [
                  isCurrencyMetric ? fmtCurrency.format(value ?? 0) : `${Math.round(value ?? 0)}`,
                  name === "comparison" ? comparisonLegendLabel : "Current",
                ]}
              />
              {hasComparison ? (
                <Line
                  type="monotone"
                  dataKey="comparison"
                  stroke="rgba(65, 59, 49, 0.45)"
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  dot={false}
                />
              ) : null}
              <Line
                type="monotone"
                dataKey="current"
                stroke="var(--sf-primary)"
                strokeWidth={2.8}
                dot={false}
                activeDot={{
                  r: 5,
                  fill: "var(--sf-primary)",
                  stroke: "rgba(39, 104, 74, 0.18)",
                  strokeWidth: 6,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="prf-trend-sidecar">
          <div className="prf-trend-sidecar__card">
            <p className="prf-sidecar__label">Current total</p>
            <p className="prf-sidecar__value">
              {isCurrencyMetric ? fmtCurrency.format(totalCurrent) : Math.round(totalCurrent)}
            </p>
            {totalDelta != null ? (
              <span className={cn("prf-delta", deltaTone(totalDelta) === "up" ? "prf-delta--up" : deltaTone(totalDelta) === "down" ? "prf-delta--down" : "prf-delta--flat")}>
                {formatDelta(totalDelta)}
              </span>
            ) : null}
          </div>

          <div className="prf-trend-sidecar__card">
            <p className="prf-sidecar__label">Watch months</p>
            {weakestPaceMonths.length > 0 ? (
              <div className="prf-watch-list">
                {weakestPaceMonths.map((row) => (
                  <div key={row.month} className="prf-watch-list__item">
                    <div>
                      <p className="prf-watch-list__title">{row.month}</p>
                      <p className="prf-watch-list__detail">
                        {fmtCurrency.format(row.current_rotb)} on books
                      </p>
                    </div>
                    <span className={cn("prf-watch-list__pill", (row.pct_of_ly ?? 100) < 85 && "prf-watch-list__pill--warning")}>
                      {row.pct_of_ly != null ? `${Math.round(row.pct_of_ly)}% of LY` : "—"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="prf-sidecar__detail">
                Month-by-month pace watch will appear as the booking curve fills in.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface ForecastPanelProps {
  forecast: ForecastData | null
  fmtCurrency: Intl.NumberFormat
  forecastModel: ForecastModel
}

function ForecastPanel({ forecast, fmtCurrency, forecastModel }: ForecastPanelProps) {
  if (!forecast) {
    return (
      <section className="prf-panel prf-panel--dark prf-forecast" id="prf-forecast">
        <div className="prf-panel__header">
          <div>
            <span className="prf-section-kicker">Forecast outlook</span>
            <h2 className="prf-panel__title">The annual range will appear here</h2>
          </div>
        </div>
        <p className="prf-panel__body prf-panel__body--inverse">
          Stayframe does not yet have enough forecast data for this property.
        </p>
      </section>
    )
  }

  if (forecast.confidence === "insufficient") {
    return (
      <section className="prf-panel prf-panel--dark prf-forecast" id="prf-forecast">
        <div className="prf-panel__header">
          <div>
            <span className="prf-section-kicker">Forecast outlook</span>
            <h2 className="prf-panel__title">{forecast.forecast_year} forecast is still building</h2>
          </div>
        </div>
        <p className="prf-panel__body prf-panel__body--inverse">
          Revenue forecasting needs more booking history before the annual range is reliable.
        </p>
      </section>
    )
  }

  const monthly = forecast.monthly || []
  const futureMonths = monthly.filter((month) => !month.is_actual)
  const avgOnBooks = futureMonths.length > 0
    ? Math.round(futureMonths.reduce((sum, month) => sum + month.pct_on_books, 0) / futureMonths.length)
    : 0
  const confirmedPct = Math.round((forecast.ytd_actual / Math.max(1, forecast.annual_forecast)) * 100)
  const chartData = monthly.map((month) => ({
    month: formatMonthLabel(month.month),
    forecast: Math.round(month.forecast),
    low: Math.round(month.low),
    high: Math.round(month.high),
    booked: Math.round(month.rotb),
  }))

  return (
    <section className="prf-panel prf-panel--dark prf-forecast" id="prf-forecast">
      <div className="prf-panel__header">
        <div>
          <span className="prf-section-kicker">Forecast outlook</span>
          <h2 className="prf-panel__title">{forecast.forecast_year} revenue range</h2>
        </div>
        <span className={cn("prf-signal prf-signal--dark", forecastModel.tone === "positive" && "prf-signal--positive", forecastModel.tone === "warning" && "prf-signal--warning")}>
          {forecastModel.label}
        </span>
      </div>

      <div className="prf-forecast__headline">
        <div>
          <p className="prf-forecast__range">
            {fmtCurrency.format(forecast.annual_low)}
            <span>—</span>
            {fmtCurrency.format(forecast.annual_high)}
          </p>
          <p className="prf-panel__body prf-panel__body--inverse">
            Base case {fmtCurrency.format(forecast.annual_forecast)}.
            {forecast.vs_ly_pct != null && forecast.ly_total > 0
              ? ` ${formatDelta(forecast.vs_ly_pct)} vs ${forecast.forecast_year - 1} actual.`
              : ""}
          </p>
        </div>
        <div className="prf-forecast__stats">
          <div className="prf-forecast__stat">
            <span>Confirmed</span>
            <strong>{confirmedPct}%</strong>
          </div>
          <div className="prf-forecast__stat">
            <span>Avg on books</span>
            <strong>{avgOnBooks}%</strong>
          </div>
          <div className="prf-forecast__stat">
            <span>History</span>
            <strong>{pluralize(forecast.data_quality.history_months, "mo")}</strong>
          </div>
        </div>
      </div>

      <div className="prf-forecast__chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(247, 242, 234, 0.08)" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: "rgba(247, 242, 234, 0.56)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide />
            <Tooltip
              contentStyle={{
                background: "rgba(17, 40, 32, 0.96)",
                border: "1px solid rgba(247, 242, 234, 0.14)",
                borderRadius: "14px",
                fontSize: "12px",
                color: "#f7f2ea",
              }}
              formatter={(value: number | undefined) => fmtCurrency.format(value ?? 0)}
            />
            <Area type="monotone" dataKey="high" stroke="none" fill="rgba(118, 196, 152, 0.12)" />
            <Area type="monotone" dataKey="low" stroke="none" fill="rgba(118, 196, 152, 0.04)" />
            <Area type="monotone" dataKey="forecast" stroke="#76c498" strokeWidth={2.4} fill="rgba(118, 196, 152, 0.14)" />
            <Area type="monotone" dataKey="booked" stroke="#e9d8aa" strokeWidth={1.6} fill="rgba(233, 216, 170, 0.08)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="prf-forecast__footer">
        <div>
          <p className="prf-panel__body prf-panel__body--inverse">
            {forecast.interpretation?.summary || forecastModel.detail}
          </p>
          {forecast.interpretation?.risks ? (
            <p className="prf-panel__meta prf-panel__meta--inverse">{forecast.interpretation.risks}</p>
          ) : null}
        </div>
        <div className="prf-forecast__meta">
          <span>{forecast.data_quality.season_classification}</span>
          <span>Updated {formatRelativeTime(forecast.computed_at)}</span>
        </div>
      </div>
    </section>
  )
}

class PerformanceErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; message: string }> {
  state = { hasError: false, message: "" }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error?.message || "Unknown performance render failure" }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("PerformancePage render failed", error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="prf-page">
          <div className="prf-shell">
            <div className="prf-error">
              <p className="prf-error__title">Performance hit an unexpected data issue</p>
              <p className="prf-error__body">
                A live data edge case interrupted the commercial dashboard render. Refresh and Stayframe will retry with safer fallbacks.
              </p>
              {this.state.message ? (
                <p className="prf-error__body">
                  <code>{this.state.message}</code>
                </p>
              ) : null}
            </div>
          </div>
        </main>
      )
    }
    return this.props.children
  }
}

function PerformancePageInner() {
  const {
    propertyId,
    property,
    from,
    to,
    period,
    periodConfig,
    setPeriod,
    setCustomRange,
    kpis,
    trend,
    comparisonTrend,
    channels,
    pacing,
    sync,
    demandBehaviour,
    insightResult,
    insightAiEnabled,
    insightsLoading,
    seasonProfile,
    forecast,
    loading,
    refreshing,
    error,
    hasData,
    fmtCurrency,
    refreshInsights,
    refreshSummary,
  } = usePerformanceData()
  const { toast } = useToast()
  useActivationEvent(propertyId, "performance_viewed")
  const { showRitual } = useIntelligenceGate({
    scope: "dashboard",
    propertyId: property?.id,
    isReady: !loading,
  })

  const [showAllActions, setShowAllActions] = useState(false)
  const [trendMetric, setTrendMetric] = useState<TrendMetricKey>("revenue")
  const [customFromDraft, setCustomFromDraft] = useState(from)
  const [customToDraft, setCustomToDraft] = useState(to)
  const [syncing, setSyncing] = useState(false)
  const [drilldownLoading, setDrilldownLoading] = useState(false)
  const [drilldownData, setDrilldownData] = useState<PerformanceDrilldownResponse | null>(null)
  const [activeDrilldown, setActiveDrilldown] = useState<DrilldownKey | null>(null)

  useEffect(() => {
    setCustomFromDraft(from)
    setCustomToDraft(to)
  }, [from, to])

  const compareText = comparisonLabel(periodConfig.isForwardLooking, periodConfig.compare)
  const comparisonLegendLabel = comparisonLegend(periodConfig.isForwardLooking, periodConfig.compare)

  const revenueDelta = getMetricDelta("revenue", { isForwardLooking: periodConfig.isForwardLooking, kpis, pacing })
  const occupancyDelta = getMetricDelta("occupancy", { isForwardLooking: periodConfig.isForwardLooking, kpis, pacing })
  const bookedNightsDelta = getMetricDelta("booked_nights", { isForwardLooking: periodConfig.isForwardLooking, kpis, pacing })
  const adrDelta = getMetricDelta("adr", { isForwardLooking: periodConfig.isForwardLooking, kpis, pacing })
  const bookingsDelta = getMetricDelta("bookings", { isForwardLooking: periodConfig.isForwardLooking, kpis, pacing })
  const paceDelta = typeof pacing?.delta_pct === "number" ? pacing.delta_pct : revenueDelta

  const shortWindowShare = getShortWindowShare(demandBehaviour)
  const topChannel = channels[0]
  const paceLabel = periodConfig.isForwardLooking ? "Booked pace" : "Current run-rate"

  const verdict = useMemo(
    () =>
      buildVerdictModel({
        revenueDelta,
        adrDelta,
        bookedNightsDelta,
        paceDelta,
        topChannel,
        shortWindowShare,
        compareLabel: compareText,
        paceLabel,
      }),
    [revenueDelta, adrDelta, bookedNightsDelta, paceDelta, topChannel, shortWindowShare, compareText, paceLabel],
  )

  const syncModel = useMemo(
    () => buildSyncModel(sync?.last_sync_at, sync?.status),
    [sync?.last_sync_at, sync?.status],
  )

  const forecastModel = useMemo(
    () => buildForecastModel(forecast, syncModel),
    [forecast, syncModel],
  )

  const heroMetrics = useMemo(
    () =>
      buildHeroMetrics({
        fmtCurrency,
        kpis,
        revenueDelta,
        paceDelta,
        adrDelta,
        occupancyDelta,
        bookingsDelta,
        pacing,
        compareLabel: compareText,
      }),
    [fmtCurrency, kpis, revenueDelta, paceDelta, adrDelta, occupancyDelta, bookingsDelta, pacing, compareText],
  )

  const diagnostics = useMemo(
    () =>
      buildDiagnostics({
        fmtCurrency,
        compareLabel: compareText,
        kpis,
        revenueDelta,
        occupancyDelta,
        bookedNightsDelta,
        adrDelta,
        paceDelta,
        channels,
        demandBehaviour,
      }),
    [fmtCurrency, compareText, kpis, revenueDelta, occupancyDelta, bookedNightsDelta, adrDelta, paceDelta, channels, demandBehaviour],
  )

  const actions = useMemo(
    () =>
      buildActions({
        propertyId,
        compareLabel: compareText,
        fmtCurrency,
        kpis,
        pacing,
        syncModel,
        forecast,
        topChannel,
        shortWindowShare,
        revenueDelta,
        bookedNightsDelta,
        occupancyDelta,
        adrDelta,
        paceDelta,
        demandBehaviour,
        insightResult,
      }),
    [
      propertyId,
      compareText,
      fmtCurrency,
      kpis,
      pacing,
      syncModel,
      forecast,
      topChannel,
      shortWindowShare,
      revenueDelta,
      bookedNightsDelta,
      occupancyDelta,
      adrDelta,
      paceDelta,
      demandBehaviour,
      insightResult,
    ],
  )

  const shiftItems = useMemo(
    () =>
      buildShiftItems({
        fmtCurrency,
        pacing,
        forecast,
        syncModel,
        seasonProfile,
      }),
    [fmtCurrency, pacing, forecast, syncModel, seasonProfile],
  )

  const briefLead = useMemo(() => buildBriefLead(verdict, insightResult), [verdict, insightResult])
  const briefDrivers = useMemo(() => buildBriefDrivers(diagnostics, insightResult), [diagnostics, insightResult])
  const channelReadout = useMemo(
    () => buildChannelReadout({ topChannel, kpis, fmtCurrency }),
    [topChannel, kpis, fmtCurrency],
  )
  const demandReadout = useMemo(
    () =>
      buildDemandReadout({
        demandBehaviour,
        shortWindowShare,
        avgLead: kpis?.avg_lead_time?.value ?? null,
      }),
    [demandBehaviour, shortWindowShare, kpis?.avg_lead_time?.value],
  )
  const compactBriefDrivers = briefDrivers.slice(0, 2)

  const defaultTrendMetric = useMemo<TrendMetricKey>(() => {
    if ((paceDelta ?? 0) <= -5 || (revenueDelta ?? 0) <= -5) return "revenue"
    if ((adrDelta ?? 0) <= -4) return "adr"
    if ((bookedNightsDelta ?? 0) <= -4) return "booked_nights"
    return "revenue"
  }, [paceDelta, revenueDelta, adrDelta, bookedNightsDelta])

  useEffect(() => {
    setTrendMetric(defaultTrendMetric)
  }, [defaultTrendMetric, period, propertyId])

  const visibleActions = showAllActions ? actions : actions.slice(0, 2)
  const primaryAction = actions[0]
  const currencyTextFormatter = useMemo(
    () => createSafeCurrencyFormatter(kpis?.revenue.currency),
    [kpis?.revenue.currency],
  )

  async function openDrilldown(kpi: DrilldownKey) {
    if (!propertyId) return
    setActiveDrilldown(kpi)
    setDrilldownLoading(true)
    setDrilldownData(null)
    const response = await apiClient.getPerformanceDrilldown(propertyId, { kpi, from, to })
    if (response.ok) {
      setDrilldownData(response)
    } else {
      toast(response.error || "Unable to load detail right now.", "error")
    }
    setDrilldownLoading(false)
  }

  function closeDrilldown() {
    setActiveDrilldown(null)
    setDrilldownData(null)
    setDrilldownLoading(false)
  }

  function scrollToSection(sectionId: string) {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  async function handleRefreshAll() {
    await Promise.all([refreshSummary(), refreshInsights()])
  }

  async function handleSync() {
    if (!propertyId || syncing) return
    setSyncing(true)
    const response = await apiClient.syncPerformanceData(propertyId)
    if (response.ok) {
      await Promise.all([refreshSummary(), refreshInsights()])
      toast(`Sync complete${response.bookings_synced != null ? ` — ${response.bookings_synced} bookings updated` : ""}`, "success")
    } else if (response.code === "rate_limited") {
      toast(response.error || "Already synced recently. Try again in a few minutes.", "info")
    } else {
      toast(response.error || "Sync failed. Please try again.", "error")
    }
    setSyncing(false)
  }

  async function handleAction(action: ActionItem) {
    if (action.target.type === "sync") {
      await handleSync()
      return
    }
    if (action.target.type === "section") {
      scrollToSection(action.target.sectionId)
      return
    }
    if (action.target.type === "drilldown") {
      await openDrilldown(action.target.kpi)
    }
  }

  function applyCustomRange() {
    if (!customFromDraft || !customToDraft || customFromDraft > customToDraft) return
    setCustomRange(customFromDraft, customToDraft)
  }

  if (loading || showRitual) {
    return (
      <main className="prf-page">
        <div className="prf-shell">
          {showRitual ? (
            <AiLoadingCard messages={PERFORMANCE_AI_MESSAGES} />
          ) : (
            <div className="prf-loading" role="status" aria-label="Loading performance dashboard">
              <div className="prf-loading__hero" />
              <div className="prf-loading__grid">
                <div className="prf-loading__panel prf-loading__panel--large" />
                <div className="prf-loading__stack">
                  <div className="prf-loading__panel" />
                  <div className="prf-loading__panel" />
                </div>
              </div>
              <div className="prf-loading__footer" />
            </div>
          )}
        </div>
      </main>
    )
  }

  if (error && !kpis) {
    return (
      <main className="prf-page">
        <div className="prf-shell">
          <div className="prf-error">
            <p className="prf-error__title">Unable to load property performance</p>
            <p className="prf-error__body">{error}</p>
            <div className="prf-error__actions">
              <Button size="sm" onClick={() => void handleRefreshAll()}>
                Refresh performance
              </Button>
            </div>
          </div>
        </div>
      </main>
    )
  }

  if (!hasData) {
    return (
      <main className="prf-page">
        <div className="prf-shell">
          <section className="prf-empty">
            <span className="prf-section-kicker">Performance</span>
            <h1 className="prf-empty__title">The commercial dashboard will fill in once bookings land.</h1>
            <p className="prf-empty__body">
              {sync?.status === "never"
                ? "Stayframe is waiting for the first booking sync before it can read revenue, pace, and forecast."
                : "There are no bookings in this period yet. Try another window or refresh the sync."}
            </p>
            <div className="prf-empty__actions">
              <Button variant="primary" size="lg" onClick={() => void handleSync()} loading={syncing}>
                Sync booking data
              </Button>
              <Button variant="secondary" size="lg" onClick={() => setPeriod("next12")}>
                Use next 12 months
              </Button>
            </div>
          </section>
        </div>
      </main>
    )
  }

  return (
    <main className="prf-page">
      <div className={cn("prf-shell", refreshing && "prf-shell--refreshing")}>
        <section className="prf-hero">
          <div className="prf-hero__content">
            <div className="prf-hero__topbar">
              <div className="prf-hero__eyebrow">
                <span
                  className={cn(
                    "prf-stage-chip",
                    verdict.tone === "ahead" && "prf-stage-chip--ahead",
                    verdict.tone === "behind" && "prf-stage-chip--behind",
                    verdict.tone === "steady" && "prf-stage-chip--steady",
                  )}
                >
                  {verdict.tone === "ahead" ? "Ahead" : verdict.tone === "behind" ? "Behind" : "Holding"}
                </span>
                <span className="prf-hero__property">{property?.name || "Property performance"}</span>
              </div>

              <div className="prf-controls">
                <div className="prf-period-tabs" role="tablist" aria-label="Performance period">
                  {PERIOD_TABS.map((tab) => (
                    <button
                      key={tab.preset}
                      type="button"
                      className={cn("prf-period-tab", period === tab.preset && "prf-period-tab--active")}
                      onClick={() => setPeriod(tab.preset)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {period === "custom" ? (
                  <div className="prf-custom-range">
                    <label className="prf-custom-range__field">
                      <span>From</span>
                      <input
                        type="date"
                        value={customFromDraft}
                        onChange={(event) => setCustomFromDraft(event.target.value)}
                      />
                    </label>
                    <label className="prf-custom-range__field">
                      <span>To</span>
                      <input
                        type="date"
                        value={customToDraft}
                        onChange={(event) => setCustomToDraft(event.target.value)}
                      />
                    </label>
                    <Button
                      size="sm"
                      onClick={applyCustomRange}
                      disabled={!customFromDraft || !customToDraft || customFromDraft > customToDraft}
                    >
                      Apply range
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="prf-hero__copy">
              <h1 className="prf-hero__headline">{verdict.headline}</h1>
              <p className="prf-hero__summary">
                {verdict.summary || "Stayframe is comparing revenue, pricing, pace, and demand quality for the selected window."}
              </p>
            </div>

            <div className="prf-morning-grid">
              <div className="prf-panel prf-panel--board prf-panel--glance" id="prf-actions">
                <div className="prf-panel__header">
                  <div>
                    <span className="prf-section-kicker">Morning glance</span>
                    <h2 className="prf-panel__title">What to do first</h2>
                  </div>
                  <div className="prf-board__controls">
                    <Button variant="ghost" size="sm" onClick={() => void handleRefreshAll()}>
                      Refresh brief
                    </Button>
                    {syncModel.isStale ? (
                      <Button variant="secondary" size="sm" onClick={() => void handleSync()} loading={syncing}>
                        Sync now
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="prf-panel__intro">
                  <p className="prf-panel__body">
                    {primaryAction
                      ? `Start with ${primaryAction.title}. Most weekly check-ins should end with the first two moves below.`
                      : "Most weekly check-ins should end with the first two moves below."}
                  </p>
                </div>

                <div className="prf-action-list">
                  {visibleActions.map((action, index) => (
                    <article
                      key={action.key}
                      className={cn(
                        "prf-action",
                        action.tone === "critical" && "prf-action--critical",
                        action.tone === "warning" && "prf-action--warning",
                        action.tone === "positive" && "prf-action--positive",
                      )}
                    >
                      <div className="prf-action__rank">{String(index + 1).padStart(2, "0")}</div>
                      <div className="prf-action__body">
                        <div className="prf-action__meta">
                          <span className="prf-action__category">{action.category}</span>
                          {action.confidence ? <span className="prf-action__confidence">{action.confidence} confidence</span> : null}
                        </div>
                        <h3 className="prf-action__title">{action.title}</h3>
                        <p className="prf-action__detail">{action.detail}</p>
                        <p className="prf-action__consequence">{action.consequence}</p>
                      </div>
                      <div className="prf-action__cta">
                        <Button
                          variant={index === 0 ? "primary" : "secondary"}
                          onClick={() => void handleAction(action)}
                          loading={action.target.type === "sync" ? syncing : false}
                        >
                          {action.ctaLabel}
                        </Button>
                      </div>
                    </article>
                  ))}
                </div>

                {actions.length > 2 ? (
                  <div className="prf-action-list__footer">
                    <Button variant="ghost" size="sm" onClick={() => setShowAllActions((value) => !value)}>
                      {showAllActions ? "Show fewer actions" : `Show all ${actions.length} actions`}
                    </Button>
                  </div>
                ) : null}
              </div>

              <aside className="prf-brief">
                <div className="prf-brief__header">
                  <div>
                    <span className="prf-brief__eyebrow">Commercial brief</span>
                    <h2 className="prf-brief__title">What this week means</h2>
                  </div>
                  <span className="prf-brief__meta">
                    {insightAiEnabled && insightResult?.meta?.generated_at
                      ? `AI updated ${formatRelativeTime(insightResult.meta.generated_at)}`
                      : "Metric-backed brief"}
                  </span>
                </div>

                <p className="prf-brief__body">{briefLead}</p>

                <div className="prf-brief__drivers">
                  {insightsLoading ? (
                    <div className="prf-brief__loading">
                      <div />
                      <div />
                    </div>
                  ) : (
                    compactBriefDrivers.map((driver, index) => (
                      <div key={`${driver.label}-${index}`} className="prf-brief__driver">
                        <span className="prf-brief__driver-label">{driver.label}</span>
                        <p>{driver.value}</p>
                      </div>
                    ))
                  )}
                </div>
              </aside>
            </div>

            <div className="prf-kpi-grid">
              {heroMetrics.map((metric) => {
                const isClickable = Boolean(metric.drilldown || metric.sectionId)
                const Tag = isClickable ? "button" : "div"
                return (
                  <Tag
                    key={metric.key}
                    type={isClickable ? "button" : undefined}
                    className={cn("prf-kpi", isClickable && "prf-kpi--interactive")}
                    onClick={
                      metric.drilldown
                        ? () => void openDrilldown(metric.drilldown!)
                        : metric.sectionId
                          ? () => scrollToSection(metric.sectionId!)
                          : undefined
                    }
                  >
                    <p className="prf-kpi__label">{metric.label}</p>
                    {metric.delta ? (
                      <p
                        className={cn(
                          "prf-kpi__delta",
                          metric.deltaTone === "up" && "prf-kpi__delta--up",
                          metric.deltaTone === "down" && "prf-kpi__delta--down",
                          metric.deltaTone === "flat" && "prf-kpi__delta--flat",
                        )}
                      >
                        {metric.delta}
                      </p>
                    ) : (
                      <p className="prf-kpi__delta prf-kpi__delta--flat">No delta read</p>
                    )}
                    <p className="prf-kpi__context">{metric.detail}</p>
                    <div className="prf-kpi__baseline">
                      <span className="prf-kpi__baseline-label">Current</span>
                      <p className="prf-kpi__value">{metric.value}</p>
                    </div>
                  </Tag>
                )
              })}
            </div>

            <div className="prf-hero__meta">
              <span className="prf-signal">
                <CalendarClock className="h-3.5 w-3.5" />
                {periodConfig.label} · {formatRangeLabel(from, to)}
              </span>
              <span className="prf-signal">
                <Gauge className="h-3.5 w-3.5" />
                {compareText}
              </span>
              <span className={cn("prf-signal", syncModel.tone === "warning" && "prf-signal--warning", syncModel.tone === "critical" && "prf-signal--critical")}>
                <RefreshCw className="h-3.5 w-3.5" />
                {syncModel.label}
              </span>
              <span className={cn("prf-signal", forecastModel.tone === "warning" && "prf-signal--warning", forecastModel.tone === "positive" && "prf-signal--positive")}>
                <ShieldAlert className="h-3.5 w-3.5" />
                {forecastModel.label}
              </span>
            </div>

            <div className="prf-secondary-metrics">
              <span className="prf-secondary-metrics__item">
                Avg lead time <strong>{Math.round(kpis?.avg_lead_time.value ?? 0)}d</strong>
              </span>
              <span className="prf-secondary-metrics__item">
                Cancellation rate <strong>{pctText(kpis?.cancellation_rate.value)}</strong>
              </span>
              <span className="prf-secondary-metrics__item">
                Avg stay <strong>{(kpis?.avg_los.value ?? 0).toFixed(1)} nights</strong>
              </span>
            </div>
          </div>
        </section>

        <section className="prf-deep-dive">
          <div className="prf-deep-dive__intro">
            <div>
              <span className="prf-section-kicker">Deep dive</span>
              <h2 className="prf-deep-dive__title">Why the week reads this way</h2>
            </div>
            <p className="prf-deep-dive__body">
              Everything below is diagnostic detail for pricing, channel mix, demand timing, and forecast planning.
            </p>
          </div>

          <section className="prf-main-grid">
            <div className="prf-panel">
              <div className="prf-panel__header">
                <div>
                  <span className="prf-section-kicker">Commercial pressure map</span>
                  <h2 className="prf-panel__title">What is actually driving the result</h2>
                </div>
              </div>

              <div className="prf-diagnostic-list">
                {diagnostics.map((item) => (
                  <div
                    key={item.key}
                    className={cn(
                      "prf-diagnostic",
                      item.tone === "critical" && "prf-diagnostic--critical",
                      item.tone === "warning" && "prf-diagnostic--warning",
                      item.tone === "positive" && "prf-diagnostic--positive",
                      item.isPrimary && "prf-diagnostic--primary",
                    )}
                  >
                    <div className="prf-diagnostic__top">
                      <div>
                        <p className="prf-diagnostic__label">{item.label}</p>
                        <h3 className="prf-diagnostic__title">{item.status}</h3>
                      </div>
                      <div className="prf-diagnostic__value-block">
                        <strong className="prf-diagnostic__value">{item.value}</strong>
                        {item.isPrimary ? <span className="prf-diagnostic__pill">Primary driver</span> : null}
                      </div>
                    </div>
                    <p className="prf-diagnostic__detail">{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="prf-sidebar">
              <div className="prf-panel">
                <div className="prf-panel__header">
                  <div>
                    <span className="prf-section-kicker">What shifted</span>
                    <h2 className="prf-panel__title">Recent movement worth noticing</h2>
                  </div>
                </div>

                <div className="prf-shift-list">
                  {shiftItems.map((item) => (
                    <div key={item.key} className="prf-shift">
                      <div>
                        <p className="prf-shift__label">{item.label}</p>
                        <p className="prf-shift__detail">{item.detail}</p>
                      </div>
                      <span
                        className={cn(
                          "prf-shift__value",
                          item.tone === "positive" && "prf-shift__value--positive",
                          item.tone === "warning" && "prf-shift__value--warning",
                          item.tone === "critical" && "prf-shift__value--critical",
                        )}
                      >
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="prf-evidence-grid">
            <TrendPanel
              activeMetric={trendMetric}
              onMetricChange={setTrendMetric}
              trend={trend}
              comparisonTrend={comparisonTrend}
              comparisonLegendLabel={comparisonLegendLabel}
              compareLabel={compareText}
              fmtCurrency={fmtCurrency}
              pacing={pacing}
              periodLabel={periodConfig.label}
            />

            <div className="prf-sidebar">
              <div className="prf-panel" id="prf-channels">
                <div className="prf-panel__header">
                  <div>
                    <span className="prf-section-kicker">Channel intelligence</span>
                    <h2 className="prf-panel__title">{channelReadout.headline}</h2>
                  </div>
                </div>

                <p className="prf-panel__body">{channelReadout.summary}</p>

                <div className="prf-channel-list">
                  {channels.slice(0, 5).map((channel) => {
                    const shareWidth = `${Math.max(8, Math.min(100, channel.pct))}%`
                    const adrGap = kpis?.adr.value
                      ? ((channel.adr - kpis.adr.value) / kpis.adr.value) * 100
                      : null
                    return (
                      <div key={channel.name} className="prf-channel-row">
                        <div className="prf-channel-row__header">
                          <p className="prf-channel-row__title">{channel.name}</p>
                          <span className="prf-channel-row__share">{Math.round(channel.pct)}%</span>
                        </div>
                        <div className="prf-channel-row__bar">
                          <span style={{ width: shareWidth }} />
                        </div>
                        <div className="prf-channel-row__metrics">
                          <span>{fmtCurrency.format(channel.revenue)}</span>
                          <span>{fmtCurrency.format(channel.adr)} ADR</span>
                          <span>{adrGap != null ? `${formatDelta(adrGap)} vs property ADR` : "ADR gap unavailable"}</span>
                          <span>{channel.lead_time_avg != null ? `${Math.round(channel.lead_time_avg)}d lead` : "Lead time unavailable"}</span>
                          <span>{channel.cancellation_rate != null ? `${pctText(channel.cancellation_rate)} cancel` : "Cancel rate unavailable"}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="prf-panel" id="prf-demand">
                <div className="prf-panel__header">
                  <div>
                    <span className="prf-section-kicker">Demand behaviour</span>
                    <h2 className="prf-panel__title">{demandReadout.headline}</h2>
                  </div>
                </div>

                <p className="prf-panel__body">{demandReadout.summary}</p>

                {demandBehaviour ? (
                  <div className="prf-demand">
                    <div className="prf-demand__block">
                      <p className="prf-demand__label">Booking window</p>
                      <div className="prf-demand__bar">
                        {demandBehaviour.lead_time_distribution.map((bucket, index) => (
                          <span
                            key={`${bucket.label}-${index}`}
                            style={{ flex: Math.max(bucket.pct, 0.4), opacity: 0.38 + index * 0.12 }}
                          />
                        ))}
                      </div>
                      <div className="prf-demand__list">
                        {demandBehaviour.lead_time_distribution.map((bucket) => (
                          <div key={bucket.label} className="prf-demand__item">
                            <span>{bucket.label}</span>
                            <strong>{bucket.pct.toFixed(1)}%</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="prf-demand__block">
                      <p className="prf-demand__label">Stay length</p>
                      <div className="prf-demand__bar">
                        {demandBehaviour.stay_length_distribution.map((bucket, index) => (
                          <span
                            key={`${bucket.label}-${index}`}
                            style={{ flex: Math.max(bucket.pct, 0.4), opacity: 0.38 + index * 0.12 }}
                          />
                        ))}
                      </div>
                      <div className="prf-demand__list">
                        {demandBehaviour.stay_length_distribution.map((bucket) => (
                          <div key={bucket.label} className="prf-demand__item">
                            <span>{bucket.label}</span>
                            <strong>{bucket.pct.toFixed(1)}%</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="prf-demand__callout">
                      <span>Weekend arrivals</span>
                      <strong>{demandBehaviour.weekend_pct.toFixed(1)}%</strong>
                    </div>
                  </div>
                ) : (
                  <div className="prf-empty-inline">
                    <TriangleAlert className="h-4 w-4" />
                    <span>Demand timing becomes clearer once the period has a broader booking sample.</span>
                  </div>
                )}
              </div>
            </div>
          </section>

          <ForecastPanel forecast={forecast} fmtCurrency={fmtCurrency} forecastModel={forecastModel} />
        </section>

        <DrilldownDrawer
          open={activeDrilldown !== null}
          onClose={closeDrilldown}
          title={
            activeDrilldown === "revenue"
              ? "Revenue detail"
              : activeDrilldown === "occupancy"
                ? "Occupancy detail"
                : "ADR detail"
          }
        >
          {drilldownLoading ? (
            <div className="prf-drawer-loading">Loading detail…</div>
          ) : !drilldownData ? (
            <div className="prf-drawer-loading">Unable to load detail.</div>
          ) : activeDrilldown === "revenue" ? (
            <RevenueDrilldown data={drilldownData} fmtCurrency={(value) => currencyTextFormatter.format(value)} />
          ) : activeDrilldown === "occupancy" ? (
            <OccupancyDrilldown data={drilldownData} fmtPct={(value) => formatPctRound(value)} />
          ) : (
            <AdrDrilldown data={drilldownData} fmtCurrency={(value) => currencyTextFormatter.format(value)} />
          )}
        </DrilldownDrawer>

        <FeedbackTrigger pageSlug="performance" />
      </div>
    </main>
  )
}

export function PerformancePage() {
  return (
    <PerformanceErrorBoundary>
      <PerformancePageInner />
    </PerformanceErrorBoundary>
  )
}
